import * as d3 from "d3";
import { useEffect, useRef } from "react";
import { ColorAddedPlayer, IPlayerComparisonData, IPlotComparisonData } from "../../types/IPlayerComparison";
import { IPlotFilter, makeStringsCapital } from "./ScattrPlotUtils";

interface ScattrPlotProps {
  playerComparisonTableData: IPlotComparisonData[];
  filteredPlayers: ColorAddedPlayer[];
  hoveredPlayer: number;
  setHoveredPlayer: React.Dispatch<React.SetStateAction<number>>;
  plotFilters: IPlotFilter;
  selectedPlayers: number[];
  acrossSortDirection: "inc" | "dec";
  upSortDirection: "desc" | "asc";
  setSelectedPlayers: React.Dispatch<React.SetStateAction<number[]>>;
  benchMarkPlayerId: number;
  playerCardID: number;
  setPlayerCardID: React.Dispatch<React.SetStateAction<number>>;
}

function formatNumber(num: number) {
  if (num >= 1000000) {
    return `${(num / 1000000)?.toFixed(0)}M`;
  } else if (num >= 1000) {
    return `${(num / 1000)?.toFixed(0)}K`;
  } else {
    return num?.toString();
  }
}

export const ScattrPlot: React.FC<ScattrPlotProps> = ({
  playerComparisonTableData,
  hoveredPlayer,
  setHoveredPlayer,
  plotFilters,
  selectedPlayers,
  filteredPlayers,
  acrossSortDirection,
  upSortDirection,
  setSelectedPlayers,
  benchMarkPlayerId,
  playerCardID,
  setPlayerCardID,
}) => {
  const minAcross = Math.min(...playerComparisonTableData?.map((player) => player[plotFilters?.across ? plotFilters?.across : "age"]));
  const maxAcross = Math.max(...playerComparisonTableData?.map((player) => player[plotFilters?.across ? plotFilters?.across : "age"]));
  const minUp = Math.min(...playerComparisonTableData?.map((player) => player[plotFilters?.up ? plotFilters?.up : "attackContribution"]));
  const maxUp = Math.max(...playerComparisonTableData?.map((player) => player[plotFilters?.up ? plotFilters?.up : "attackContribution"]));
  const minDotSize = Math.min(
    ...playerComparisonTableData?.map((player) => player[plotFilters?.dotSize ? plotFilters?.dotSize : "marketValue"])
  );
  const maxDotSize = Math.max(
    ...playerComparisonTableData?.map((player) => player[plotFilters?.dotSize ? plotFilters?.dotSize : "marketValue"])
  );
  const svgRef = useRef(null);

  useEffect(() => {
    const container = document.getElementById("plot-graph")!;
    const pitchWidth: number = container.clientWidth;
    const pitchHeight: number = pitchWidth * 0.7;
    const svg: d3.Selection<SVGSVGElement, unknown, any, undefined> = d3
      .select<SVGSVGElement, unknown>(svgRef.current!)
      .attr("width", pitchWidth)
      .attr("height", pitchHeight)
      .on("click", () => setSelectedPlayers([]));
    const numRows = 10;
    const numCols = 10;
    const data = d3.range(numRows * numCols).map((i) => ({
      x: (Math.floor(i % numCols) * pitchWidth) / 10,
      y: (Math.floor(i / numCols) * pitchHeight) / 10,
    }));
    svg.selectAll("*").remove();
    const gridGroup = svg.append("g").attr("transform", "translate(0, 0)");
    svg
      .append("rect")
      .attr("width", pitchWidth)
      .attr("height", pitchHeight)
      .attr("stroke-width", 1)
      .attr("stroke", "#DADADA")
      .attr("rx", 8)
      .style("fill", "transparent");
    gridGroup
      .selectAll("rect")
      .data(data)
      .enter()
      .append("rect")
      .attr("x", (d) => d.x)
      .attr("y", (d) => d.y)
      .attr("width", pitchWidth / 10)
      .attr("height", pitchHeight / 10)
      .attr("fill", (d, i) => {
        const row = Math.floor(i / numCols);
        const col = i % numCols;
        return (row + col) % 2 === 0 ? "#F9FAF7" : "#F2F5EE";
      });
    const xScale = d3
      .scaleLinear()
      .domain(acrossSortDirection === "inc" ? [maxAcross, minAcross] : [minAcross, maxAcross])
      .range([pitchWidth - 20, 20]);
    const yScale = d3
      .scaleLinear()
      .domain(upSortDirection === "desc" ? [minUp, maxUp] : [maxUp, minUp])
      .range([pitchHeight - 20, 20]);
    const zScale = d3.scaleLinear().domain([minDotSize, maxDotSize]).range([6, 40]);
    const brushGroup = svg.append("g").attr("class", "brush");
    const brush = d3
      .brush()
      .extent([
        [0, 0],
        [pitchWidth, pitchHeight],
      ])
      .on("start", function () {
        d3.select(this).attr("class", "selecting");
        d3.select(".scattr-plot-tooltip").style("visibility", "hidden");
      })
      .on("end", (event) => {
        if (!event.selection) return;
        const [[x0, y0], [x1, y1]] = event.selection;
        const selectedCircles = filteredPlayers.filter((player) => {
          const cx = plotFilters?.across ? xScale(player[plotFilters?.across]) : 0;
          const cy = plotFilters?.across ? yScale(player[plotFilters?.up!]) : yScale(player?.attackContribution);
          return cx >= x0 && cx <= x1 && cy >= y0 && cy <= y1;
        });
        const handleSelectedCircles = (selectedCircles: any[]) => {
          const selectedPlayerIds = selectedCircles.map((player) => player.playerId);
          setSelectedPlayers((prevSelected) => {
            const allAlreadySelected = selectedPlayerIds.every((id) => prevSelected.includes(id));
            if (allAlreadySelected) {
              return prevSelected.filter((id) => !selectedPlayerIds.includes(id));
            } else {
              const newSelected = Array.from(new Set([...prevSelected, ...selectedPlayerIds]));
              return newSelected;
            }
          });
        };
        handleSelectedCircles(selectedCircles);
        brushGroup.call(brush.move, null);
      });
    brushGroup.call(brush);
    const circleGroup = svg.append("g");
    let hoverTimeout: NodeJS.Timeout;
    filteredPlayers?.map((player, i) =>
      circleGroup
        .append("circle")
        .attr(
          "class",
          `plot-player plot-player-${player?.playerId} ${player?.playerId === benchMarkPlayerId ? "plot-player-benchmark" : ""}`
        )
        .attr("cx", plotFilters?.across ? xScale(player[plotFilters?.across]) : 20)
        .attr("cy", plotFilters?.up ? yScale(player[plotFilters?.up!]) : 20)
        .style(
          "fill",
          selectedPlayers?.includes(player?.playerId)
            ? "#4AF075"
            : player?.playerId === benchMarkPlayerId
            ? "white"
            : hoveredPlayer === 0 || hoveredPlayer === player?.playerId
            ? player?.colorCode
            : "white"
        )
        .style("stroke-width", 2)
        .style("cursor", "pointer")
        .style(
          "stroke",
          player?.playerId === benchMarkPlayerId ? "#00D1A8" : selectedPlayers?.includes(player?.playerId) ? "#4AF075" : player?.colorCode
        )
        .attr("r", plotFilters?.dotSize ? zScale(player[plotFilters?.dotSize]) : 6)
        .on("mouseenter", function () {
          if (d3.select(brushGroup.node()).classed("selecting")) return;
          hoverTimeout = setTimeout(() => {
            setHoveredPlayer(player?.playerId);
          }, 500);
        })
        .on("mouseout", function () {
          clearTimeout(hoverTimeout);
          setHoveredPlayer(0);
          d3.select(".scattr-plot-tooltip").style("visibility", "hidden");
        })
        .on("click", function () {
          if (playerCardID === player?.playerId) {
            setPlayerCardID(0);
          } else {
            setPlayerCardID(player?.playerId);
          }
        })
    );
    d3.select("body")
      .append("div")
      .attr("class", "scattr-plot-tooltip")
      .style("position", "absolute")
      .style("visibility", "hidden")
      .style("background-color", "white")
      .style("border-radius", "2px");
  }, [playerComparisonTableData, hoveredPlayer, plotFilters, selectedPlayers, filteredPlayers, upSortDirection, acrossSortDirection]);

  useEffect(() => {
    if (hoveredPlayer > 0) {
      const player = filteredPlayers?.find((player) => player?.playerId === hoveredPlayer);
      const playerCircle = d3.select(`.plot-player-${player?.playerId}`);
      const radius = Number(playerCircle?.attr("r"));
      // @ts-ignore
      const boundingRect = playerCircle?.node()!.getBoundingClientRect();
      const x = Math.floor(boundingRect.left + window.scrollX);
      const y = Math.floor(boundingRect.top + window.scrollY);
      const leftOffset = radius > 35 ? -20 : radius > 17 ? -2 : radius > 12 ? 3 : radius > 10 ? 5 : radius > 9 ? 5 : 10;
      d3
        .select(".scattr-plot-tooltip")
        .style("visibility", "visible")
        .style("top", y - 70 + "px")
        .style("left", x - leftOffset + "px").html(`
            <div>
                <h4>${player?.playerLabel}</h4>
                <div>
                    ${
                      plotFilters?.up
                        ? `<p>${makeStringsCapital(plotFilters?.up)} - <span>${formatNumber(player![plotFilters?.up])}</span></p>`
                        : ``
                    }
                    ${
                      plotFilters?.across
                        ? `<p>${makeStringsCapital(plotFilters?.across)} - <span>${formatNumber(player![plotFilters?.across])}</span></p>`
                        : ``
                    }
                    ${
                      plotFilters?.dotSize
                        ? `<p>${makeStringsCapital(plotFilters?.dotSize)} - <span>${formatNumber(player![plotFilters?.dotSize])}</span></p>`
                        : ``
                    }
                </div>
            </div  
        `);
    } else {
      d3.select(".scattr-plot-tooltip").style("visibility", "hidden");
    }
  }, [hoveredPlayer, playerComparisonTableData]);

  useEffect(() => {
    const handleMouseMove = (event: any) => {
      const circles = document.querySelectorAll(".plot-player");
      const plotPlayerTab = document.querySelectorAll(".plot-player-tab");
      let isOverPlayerTab = false;
      let isOverCircle = false;
      circles.forEach((circle) => {
        const rect = circle.getBoundingClientRect();
        if (event.clientX >= rect.left && event.clientX <= rect.right && event.clientY >= rect.top && event.clientY <= rect.bottom) {
          isOverCircle = true;
        }
      });
      plotPlayerTab.forEach((tab) => {
        const rect = tab.getBoundingClientRect();
        if (event.clientX >= rect.left && event.clientX <= rect.right && event.clientY >= rect.top && event.clientY <= rect.bottom) {
          isOverPlayerTab = true;
        }
      });

      if (!isOverCircle && !isOverPlayerTab) {
        setHoveredPlayer(0);
        d3.select(".scattr-plot-tooltip").style("visibility", "hidden");
      }
    };
    document.addEventListener("mousemove", handleMouseMove);
    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
    };
  }, []);

  return (
    <div className="plot-graph" id="plot-graph">
      {plotFilters?.up && (
        <div className="plot-y-axis-label">
          {Array.from({ length: 10 + 1 }, (_, i) => (
            <>
              {upSortDirection === "desc" && <span key={i}>{formatNumber(Math.floor(maxUp - i * ((maxUp - minUp) / 10)))}</span>}
              {upSortDirection === "asc" && <span key={i}>{formatNumber(Math.floor(minUp + i * ((maxUp - minUp) / 10)))}</span>}
            </>
          ))}
        </div>
      )}
      <svg ref={svgRef}></svg>
      {plotFilters?.across && (
        <div className="plot-x-axis-label">
          {Array.from({ length: 10 + 1 }, (_, i) => (
            <>
              {acrossSortDirection === "inc" && (
                <span key={i}>{formatNumber(Math.floor(minAcross + i * ((maxAcross - minAcross) / 10)))}</span>
              )}
              {acrossSortDirection === "dec" && (
                <span key={i}>{formatNumber(Math.floor(maxAcross - i * ((maxAcross - minAcross) / 10)))}</span>
              )}
            </>
          ))}
        </div>
      )}
    </div>
  );
};
